/**
 * An action plan an agent proposed for a gap or risk found in the conversation (Andres, 2026-09-24).
 * The IT lead can adjust it and add it to the firm's action plans; nothing is created until they click.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, useApiClient } from '../api';

export interface ProposedPlan {
  theme: string;
  objective: string;
  controls: string[];
  priority: 'high' | 'medium' | 'low';
  why: string;
  /** Control assistant (5D): the IT lead asked for it, or the conversation held the detail. */
  basis?: 'asked' | 'conversation';
}

const PRIORITY = { high: 'High', medium: 'Medium', low: 'Low' } as const;

/** Why the card is here, in one line (DEC-49). */
export function suggestedBecause(basis: ProposedPlan['basis'], what: string): string {
  return basis === 'asked' ? `Suggested because you asked for ${what}.` : basis === 'conversation' ? `Suggested because the conversation now has the detail ${what} needs.` : 'Suggested from this conversation.';
}

export function PlanProposal({ firmCode, plan, canAdd, onDone, onAdded, onChanged }: {
  firmCode: string;
  plan: ProposedPlan;
  canAdd: boolean;
  onDone: () => void;
  /** Called with the new plan code; when given, the caller shows the confirmation instead. */
  onAdded?: (code: string) => void;
  /** Called once the plan is added, so the page around the card shows it (CP-04). */
  onChanged?: () => void;
}) {
  const call = useApiClient();
  const [f, setF] = useState({ objective: plan.objective, owner: '', targetDate: '', priority: plan.priority });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<string | null>(null);

  if (added) {
    return (
      <p className="assistant-note" role="status">
        Added as {added}. <Link to={`/firm/${firmCode}`}>See it on the dashboard</Link>.
      </p>
    );
  }

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await call<{ id: number; code: string }>(`/firms/${firmCode}/action-plans`, {
        method: 'POST',
        body: { theme: plan.theme, objective: f.objective, controls: plan.controls, priority: f.priority, owner: f.owner || null, targetDate: f.targetDate || null },
      });
      onChanged?.();
      if (onAdded) onAdded(r.code);
      else setAdded(r.code);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The plan was not added.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="proposal plan-proposal" aria-label="Add this to your action plans?">
      {/* The control assistant says why it suggested the plan (DEC-49); pages that build a plan from a click do not. */}
      {plan.basis && <p className="proposal-kicker">Suggestion from the assistant. Not added yet.</p>}
      <h3>Track this as an action plan?</h3>
      {plan.basis && (
        <p className="proposal-why small">
          {suggestedBecause(plan.basis, 'an action plan')} {canAdd ? 'Nothing is added until you click Add to action plans.' : "Nothing is added unless your firm's IT lead adds it."}
        </p>
      )}
      {plan.why && <p className="small">{plan.why}</p>}
      <p className="small">
        {/* NEXT PASS 3: plain phrasing in place of a middle-dot pair. */}
        <strong>{plan.theme}</strong>
        {plan.controls.length > 0 && <>. It helps meet {plan.controls.map((c) => (c.startsWith('E8-') ? 'E8' : `CIS ${c}`)).join(', ')}.</>}
      </p>
      {canAdd ? (
        <>
          <label>
            Objective
            <textarea rows={3} maxLength={600} value={f.objective} onChange={(e) => setF({ ...f, objective: e.target.value })} />
          </label>
          <div className="row">
            <label>
              Owner (optional)
              <input maxLength={200} value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} />
            </label>
            <label>
              Target date (optional)
              <input type="date" value={f.targetDate} onChange={(e) => setF({ ...f, targetDate: e.target.value })} />
            </label>
            <label>
              Priority
              <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value as ProposedPlan['priority'] })}>
                {Object.entries(PRIORITY).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="actions">
            <button type="button" className="pill" disabled={busy || f.objective.trim().length < 10} onClick={() => void add()}>
              {busy ? 'Adding…' : 'Add to action plans'}
            </button>
            <button type="button" className="pill secondary-dark" disabled={busy} onClick={onDone}>
              Not now
            </button>
          </div>
        </>
      ) : (
        <p className="assistant-note">Your firm's IT lead can add this to the firm's action plans.</p>
      )}
      {error && (
        <p role="alert" className="assistant-error">
          {error}
        </p>
      )}
    </section>
  );
}
